/**
 * This is the entrypoint for the Evaluation Comparison Page.
 */

import {Box} from '@material-ui/core';
import {Alert, Tooltip} from '@mui/material';
import {MOON_250} from '@wandb/weave/common/css/globals.styles';
import {Icon} from '@wandb/weave/components/Icon';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {maybePluralizeWord} from '@wandb/weave/core/util/string';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {AutoSizer} from 'react-virtualized';

import {Button} from '../../../../../Button';
import {
  useBackNavigation,
  useWeaveflowCurrentRouteContext,
  WeaveflowPeekContext,
} from '../../context';
import {CustomWeaveTypeProjectContext} from '../../typeViews/CustomWeaveTypeDispatcher';
import {
  SimplePageLayout,
  SimplePageLayoutContext,
  SimpleTabView,
} from '../common/SimplePageLayout';
import {useComparisonView} from '../SavedViews/ComparisonViewContext';
import {ComparisonViewSuffix} from '../SavedViews/ComparisonViewSuffix';
import {DropdownSwitchComparisonView} from '../SavedViews/DropdownSwitchComparisonView';
import {ViewName} from '../SavedViews/ViewName';
import {ViewNameEditing} from '../SavedViews/ViewNameEditing';
import {
  CompareEvaluationsProvider,
  useCompareEvaluationsState,
} from './compareEvaluationsContext';
import {STANDARD_PADDING} from './ecpConstants';
import {EvaluationComparisonState} from './ecpState';
import {ComparisonDimensionsType} from './ecpState';
import {ScoreFilter} from './ecpState';
import {EvaluationCall} from './ecpTypes';
import {EVALUATION_NAME_DEFAULT} from './ecpUtil';
import {VerticalBox} from './Layout';
import {ComparisonDefinitionSection} from './sections/ComparisonDefinitionSection/ComparisonDefinitionSection';
import {ExampleCompareSectionDetailGuarded} from './sections/ExampleCompareSection/ExampleCompareSectionDetail';
import {ExampleCompareSectionTable} from './sections/ExampleCompareSection/ExampleCompareSectionTable';
import {ExampleFilterSection} from './sections/ExampleFilterSection/ExampleFilterSection';
import {ScorecardSection} from './sections/ScorecardSection/ScorecardSection';
import {SummaryPlots} from './sections/SummaryPlotsSection/SummaryPlotsSection';

// Default percentages of horizontal space to allocate to the table view when the detail view is shown.
const SINGLE_EVAL_SPLIT_POSITION = 50;
const MULTI_EVAL_SPLIT_POSITION = 30;

type TabType = 'summary' | 'results';

type CompareEvaluationsPageProps = {
  entity: string;
  project: string;
  evaluationCallIds: string[];
  onEvaluationCallIdsUpdate: (newEvaluationCallIds: string[]) => void;
  selectedMetrics: Record<string, boolean> | null;
  setSelectedMetrics: (newModel: Record<string, boolean>) => void;
  scoreFilters?: ScoreFilter[];
  setScoreFilters?: (filters: ScoreFilter[]) => void;
  initialTabValue?: TabType;
  onTabChange?: (tab: TabType) => void;
  hideEvalPicker?: boolean;
};

export const CompareEvaluationsPage: React.FC<
  CompareEvaluationsPageProps
> = props => {
  const {isPeeking} = useContext(WeaveflowPeekContext);
  const {views, viewId, onLoadView, baseView, onSaveTitle, viewsLoading} =
    useComparisonView();
  const [isEditingName, setIsEditingName] = useState(false);

  const headerPrefix = useMemo(() => {
    const elements = [];

    if (!isPeeking) {
      elements.push(
        <ReturnToEvaluationsButton
          key="return-button"
          entity={props.entity}
          project={props.project}
        />
      );
    }

    if (viewsLoading || (views && views.length > 0)) {
      if (viewsLoading || views.length === 0) {
        elements.push(<div key="view-dropdown" className="-ml-8 h-[32px]" />);
      } else {
        elements.push(
          <div key="view-dropdown" className="-ml-8">
            <DropdownSwitchComparisonView
              views={views}
              currentViewId={viewId}
              currentViewerId={null}
              onLoadView={onLoadView}
            />
          </div>
        );
      }
    }

    return elements.length > 0 ? <>{elements}</> : null;
  }, [
    isPeeking,
    props.entity,
    props.project,
    views,
    viewsLoading,
    viewId,
    onLoadView,
  ]);

  const headerSuffix = useMemo(() => {
    if (viewsLoading) {
      return null;
    }

    return (
      <Tailwind>
        <ComparisonViewSuffix isReadonly={false} />
      </Tailwind>
    );
  }, [viewsLoading]);

  const activeName = useMemo(() => {
    if (baseView?.val.label) {
      return baseView.val.label;
    }
    return props.evaluationCallIds.length === 1
      ? 'Evaluation results'
      : 'Compare evaluations';
  }, [baseView, props.evaluationCallIds.length]);

  const canEditName = views && views.length > 0;

  const onNameChanged = useCallback(
    (newName: string) => {
      onSaveTitle(newName);
    },
    [onSaveTitle]
  );

  const pageTitle = useMemo(() => {
    if (!canEditName) {
      return activeName;
    }

    return (
      <Tailwind>
        {isEditingName ? (
          <ViewNameEditing
            value={activeName}
            onChanged={onNameChanged}
            onExit={() => setIsEditingName(false)}
          />
        ) : (
          <ViewName
            value={activeName}
            onEditNameStart={() => setIsEditingName(true)}
            tooltip="Click to rename view"
          />
        )}
      </Tailwind>
    );
  }, [canEditName, isEditingName, activeName, onNameChanged]);

  return (
    <SimplePageLayoutContext.Provider value={{headerPrefix, headerSuffix}}>
      <SimplePageLayout
        title={pageTitle}
        hideTabsIfSingle
        tabs={[
          {
            label: 'All',
            content: (
              <CompareEvaluationsPageContent
                entity={props.entity}
                project={props.project}
                evaluationCallIds={props.evaluationCallIds}
                onEvaluationCallIdsUpdate={props.onEvaluationCallIdsUpdate}
                selectedMetrics={props.selectedMetrics}
                setSelectedMetrics={props.setSelectedMetrics}
                scoreFilters={props.scoreFilters}
                setScoreFilters={props.setScoreFilters}
                initialTabValue={props.initialTabValue}
                onTabChange={props.onTabChange}
                hideEvalPicker={props.hideEvalPicker}
              />
            ),
          },
        ]}
      />
    </SimplePageLayoutContext.Provider>
  );
};

export const CompareEvaluationsPageContent: React.FC<
  CompareEvaluationsPageProps
> = props => {
  const [comparisonDimensions, setComparisonDimensions] =
    React.useState<ComparisonDimensionsType | null>(null);

  const [selectedInputDigest, setSelectedInputDigest] = React.useState<
    string | null
  >(null);

  const setComparisonDimensionsAndClearInputDigest = useCallback(
    (
      dimensions:
        | ComparisonDimensionsType
        | null
        | ((
            prev: ComparisonDimensionsType | null
          ) => ComparisonDimensionsType | null)
    ) => {
      if (typeof dimensions === 'function') {
        dimensions = dimensions(comparisonDimensions);
      }
      setComparisonDimensions(dimensions);
      setSelectedInputDigest(null);
    },
    [comparisonDimensions]
  );

  if (props.evaluationCallIds.length === 0) {
    return <div>No evaluations to compare</div>;
  }

  return (
    <CompareEvaluationsProvider
      entity={props.entity}
      project={props.project}
      initialEvaluationCallIds={props.evaluationCallIds}
      selectedMetrics={props.selectedMetrics}
      setSelectedMetrics={props.setSelectedMetrics}
      comparisonDimensions={comparisonDimensions ?? undefined}
      onEvaluationCallIdsUpdate={props.onEvaluationCallIdsUpdate}
      setComparisonDimensions={setComparisonDimensionsAndClearInputDigest}
      selectedInputDigest={selectedInputDigest ?? undefined}
      setSelectedInputDigest={setSelectedInputDigest}
      scoreFilters={props.scoreFilters}
      setScoreFilters={props.setScoreFilters ?? (() => {})}>
      <CustomWeaveTypeProjectContext.Provider
        value={{entity: props.entity, project: props.project}}>
        <CompareEvaluationsPageInner
          evaluationCallIds={props.evaluationCallIds}
          initialTabValue={props.initialTabValue}
          onTabChange={props.onTabChange}
          hideEvalPicker={props.hideEvalPicker}
        />
      </CustomWeaveTypeProjectContext.Provider>
    </CompareEvaluationsProvider>
  );
};

const ReturnToEvaluationsButton: FC<{entity: string; project: string}> = ({
  entity,
  project,
}) => {
  const router = useWeaveflowCurrentRouteContext();
  const backNavigation = useBackNavigation();
  const onClick = useCallback(() => {
    backNavigation(router.evaluationsUIUrl(entity, project));
  }, [backNavigation, router, entity, project]);
  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
      }}>
      <Tooltip
        title="Return to evaluations"
        placement="bottom"
        arrow
        slotProps={{
          tooltip: {
            sx: {
              fontFamily: 'Source Sans Pro',
            },
          },
        }}>
        <Button
          className="mr-8"
          size="medium"
          variant="ghost"
          onClick={onClick}
          icon="back"
        />
      </Tooltip>
    </Box>
  );
};

const CompareEvaluationsPageInner: React.FC<{
  evaluationCallIds: string[];
  initialTabValue?: TabType;
  onTabChange?: (tab: TabType) => void;
  hideEvalPicker?: boolean;
}> = props => {
  const {onTabChange} = props;
  const {state, setSelectedMetrics} = useCompareEvaluationsState();
  const {isPeeking} = useContext(WeaveflowPeekContext);
  const showExamples =
    Object.keys(state.loadableComparisonResults.result?.resultRows ?? {})
      .length > 0;
  const resultsLoading = state.loadableComparisonResults.loading;
  const [tabValue, setTabValue] = useState<TabType>(
    props.initialTabValue ?? 'summary'
  );

  useEffect(() => {
    if (props.initialTabValue != null) {
      setTabValue(props.initialTabValue);
    }
  }, [props.initialTabValue]);

  const handleTabChange = useCallback(
    (newValue: string) => {
      const tabValue = newValue as TabType;
      setTabValue(tabValue);
      if (onTabChange) {
        onTabChange(tabValue);
      }
    },
    [onTabChange]
  );

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'auto',
      }}>
      <SimpleTabView
        headerContent={
          <>
            <InvalidEvaluationBanner
              evaluationCalls={Object.values(state.summary.evaluationCalls)}
            />
            {!props.hideEvalPicker && (
              <ComparisonDefinitionSection state={state} />
            )}
          </>
        }
        headerContainerSx={{
          // Nice scrolling behavior
          pr: 0,
          pl: 0,
        }}
        tabs={[
          {
            value: 'summary',
            label: 'Summary',
            content: (
              <VerticalBox
                sx={{
                  height: '100%',
                  overflow: 'auto',
                  paddingTop: STANDARD_PADDING / 2,
                  alignItems: 'flex-start',
                  gridGap: STANDARD_PADDING,
                }}>
                {props.evaluationCallIds.length > 1 && (
                  <SummaryPlots
                    state={state}
                    setSelectedMetrics={setSelectedMetrics}
                  />
                )}
                <ScorecardSection state={state} />
                {!isPeeking && (
                  <Tailwind style={{width: '100%'}}>
                    <div className="px-16">
                      <div className="flex flex w-full items-center gap-3 rounded-lg bg-moon-100 px-16 py-8">
                        <Icon name="table" size="large" color="moon-500 mb-4" />
                        <p className="ml-[8px] text-[14px] font-semibold">
                          Looking for your evaluation results?
                        </p>
                        <p className="ml-[8px] mr-auto text-[14px] text-moon-500">
                          You can find it in our new results tab.
                        </p>
                        <Button
                          variant="ghost"
                          onClick={() => handleTabChange('results')}>
                          Review evaluation results
                        </Button>
                      </div>
                      <div className="h-16"></div>
                    </div>
                  </Tailwind>
                )}
              </VerticalBox>
            ),
          },
          {
            value: 'results',
            label: 'Dataset results',
            loading: resultsLoading,
            content: (
              <VerticalBox
                sx={{
                  height: '100%',
                  overflow: 'auto',
                  alignItems: 'flex-start',
                  gridGap: STANDARD_PADDING * 2,
                }}>
                {resultsLoading ? (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '50px',
                    }}>
                    <WaveLoader size="small" />
                  </Box>
                ) : showExamples ? (
                  <AutoSizer style={{height: '100%', width: '100%'}}>
                    {({height, width}) => {
                      return <ResultExplorer state={state} height={height} />;
                    }}
                  </AutoSizer>
                ) : (
                  <VerticalBox
                    sx={{
                      paddingLeft: STANDARD_PADDING,
                      paddingRight: STANDARD_PADDING,
                      paddingTop: STANDARD_PADDING,
                      width: '100%',
                      overflow: 'auto',
                    }}>
                    <Alert severity="info">
                      The selected evaluations' datasets have 0 rows in common,
                      try comparing evaluations with datasets that have at least
                      one row in common.
                    </Alert>
                  </VerticalBox>
                )}
              </VerticalBox>
            ),
          },
        ]}
        tabValue={tabValue}
        handleTabChange={handleTabChange}
      />
    </Box>
  );
};

const ResultExplorer: React.FC<{
  state: EvaluationComparisonState;
  height: number;
}> = ({state, height}) => {
  const isSingleEvaluation = state.evaluationCallIdsOrdered.length === 1;
  const [viewMode, setViewMode] = useState<'table' | 'split'>('table');
  const [splitPosition, setSplitPosition] = useState(
    isSingleEvaluation ? SINGLE_EVAL_SPLIT_POSITION : MULTI_EVAL_SPLIT_POSITION
  );
  const isDraggingRef = React.useRef(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const regressionFinderEnabled = state.evaluationCallIdsOrdered.length === 2;

  const shouldShowDetail = useMemo(() => viewMode === 'split', [viewMode]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const newPosition =
      ((e.clientX - containerRect.left) / containerRect.width) * 100;

    setSplitPosition(Math.min(Math.max(newPosition, 20), 80));
  }, []);

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    if (containerRef.current) {
      containerRef.current.style.userSelect = 'auto';
    }
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = useCallback(() => {
    isDraggingRef.current = true;
    if (containerRef.current) {
      containerRef.current.style.userSelect = 'none';
    }
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseMove, handleMouseUp]);

  return (
    <VerticalBox
      sx={{
        height: '100%',
        width: '100%',
        overflow: 'auto',
      }}>
      {regressionFinderEnabled && <ExampleFilterSection state={state} />}
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          flexDirection: 'row',
          height: height,
          borderTop: '1px solid #e0e0e0',
          position: 'relative',
        }}>
        <Box
          sx={{
            width: viewMode === 'split' ? `${splitPosition}%` : '100%',
            height: '100%',
            overflow: 'hidden',
          }}>
          <ExampleCompareSectionTable
            state={state}
            shouldHighlightSelectedRow={viewMode === 'split'}
            onShowSplitView={() => setViewMode('split')}
          />
        </Box>

        {shouldShowDetail && (
          <>
            <div
              onMouseDown={handleMouseDown}
              style={{
                width: '1px',
                height: '100%',
                cursor: 'col-resize',
                backgroundColor: MOON_250,
                position: 'relative',
                zIndex: 2,
              }}
            />
            <Box
              sx={{
                width: `${100 - splitPosition}%`,
                height: '100%',
                overflow: 'hidden',
                borderLeft: '1px solid #e0e0e0',
              }}>
              <ExampleCompareSectionDetailGuarded
                state={state}
                onClose={() => setViewMode('table')}
              />
            </Box>
          </>
        )}
      </div>
    </VerticalBox>
  );
};

/*
 * Returns true if the evaluation call has summary metrics.
 */
const isValidEval = (evalCall: EvaluationCall) => {
  return Object.keys(evalCall.summaryMetrics).length > 0;
};

const InvalidEvaluationBanner: React.FC<{
  evaluationCalls: EvaluationCall[];
}> = ({evaluationCalls}) => {
  const [dismissed, setDismissed] = useState(false);
  const invalidEvals = useMemo(() => {
    return Object.values(evaluationCalls)
      .filter(call => !isValidEval(call))
      .map(call =>
        call.name !== EVALUATION_NAME_DEFAULT
          ? call.name
          : call.callId.slice(-4)
      );
  }, [evaluationCalls]);
  if (invalidEvals.length === 0 || dismissed) {
    return null;
  }
  return (
    <Box
      sx={{
        width: '100%',
        paddingLeft: STANDARD_PADDING,
        paddingRight: STANDARD_PADDING,
      }}>
      <Tailwind>
        <Alert
          severity="info"
          classes={{
            root: 'bg-teal-300/[0.30] text-teal-600',
            action: 'text-teal-600',
          }}
          action={
            <Button
              // override the default tailwind classes for text and background hover
              className="text-override hover:bg-override"
              variant="ghost"
              onClick={() => setDismissed(true)}>
              Dismiss
            </Button>
          }>
          <span style={{fontWeight: 'bold'}}>
            No summary information found for{' '}
            {maybePluralizeWord(invalidEvals.length, 'evaluation')}:{' '}
            {invalidEvals.join(', ')}.
          </span>
        </Alert>
      </Tailwind>
    </Box>
  );
};
